import React, { useState } from 'react'
import { Button, Dialog, DialogContent, DialogTitle, FormControl, MenuItem } from '@mui/material'
import { useCreateDisbursementFeeMutation } from '../../../api-services/payments/paymentsApi'
import { type CreateDisbursementFeePayload } from '../../../api-schema-types/payments/DisbursementFeePayloadDtos'
import { useAppDispatch } from '../../../app/hooks'
import { setNotification } from '../ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { DisbursementSchedule } from '../../../api-schema-types/payments/DisbursementSchedule'
import { Formik } from 'formik'
import Grid from '@mui/material/Grid'
import ErrorComponent from '../ui-widgets/ErrorComponent'
import Select from '@mui/material/Select'
import FormikTextField from '../ui-widgets/FormikTextField'
import ButtonLoadingComponent from '../../../views/components/shared/ButtonLoadingComponent'
import InputLabel from '@mui/material/InputLabel'

enum CurrencyCodeLookup {
  ZAR = 'ZAR',
  XBT = 'XBT'
}

const CreateDisbursementFeeComponent: React.FC = () => {
  const dispatch = useAppDispatch()

  const [apiCreateFee, { isLoading }] = useCreateDisbursementFeeMutation()

  const [isDialogOpen, toggleDialog] = useState<boolean>(false)

  const handleCreateDisbursementFee = (payload: CreateDisbursementFeePayload): void => {
    apiCreateFee(payload)
      .unwrap()
      .then(() => {
        dispatch(setNotification({ open: true, severity: 'success', message: 'Disbursement Fee has been successfully created.' }))
      })
      .catch(error => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(error) }))
      })
  }

  const defaultForm: CreateDisbursementFeePayload = {
    payoutSchedule: DisbursementSchedule.WEEKLY,
    amount: 0,
    currencyCode: ''
  }

  return (
    <>
      <Button
        variant={'contained'}
        color={'primary'}
        onClick={() => { toggleDialog(true) }}
      >
        Create Disbursement Fee
      </Button>

      <Dialog
        open={isDialogOpen}
        maxWidth={'sm'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => { toggleDialog(false) }}
      >
        <Formik
          initialValues={defaultForm}
          onSubmit={(values) => { handleCreateDisbursementFee(values) }}
        >
          {formikProps => (
            <form
              autoComplete={'off'}
              autoCorrect={'off'}
              onSubmit={formikProps.handleSubmit}
            >
              <DialogTitle>Create Disbursement Fee</DialogTitle>
              <DialogContent>
                {!formikProps.isValid && (
                  <Grid item md={12} sx={{ mt: 3 }}>
                    <ErrorComponent title={'Form validation error'} message={Object.values(formikProps.errors).join(', ')}/>
                  </Grid>
                )}

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Payout Schedule</InputLabel>
                      <Select
                        fullWidth={true}
                        label="Payout Schedule"
                        error={formikProps.errors.payoutSchedule !== undefined}
                        {...formikProps.getFieldProps('payoutSchedule')}
                        onChange={(event: any) => { formikProps.setFieldValue('payoutSchedule', event.target.value) } }
                      >
                        <MenuItem value={DisbursementSchedule.WEEKLY}>{DisbursementSchedule.WEEKLY}</MenuItem>
                        <MenuItem value={DisbursementSchedule.IMMEDIATE}>{DisbursementSchedule.IMMEDIATE}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        fullWidth={true}
                        label="Currency"
                        error={formikProps.errors.currencyCode !== undefined}
                        {...formikProps.getFieldProps('currencyCode')}
                        onChange={(event: any) => { formikProps.setFieldValue('currencyCode', event.target.value) }}
                      >
                        <MenuItem value={CurrencyCodeLookup.ZAR}>{CurrencyCodeLookup.ZAR}</MenuItem>
                        <MenuItem value={CurrencyCodeLookup.XBT}>{CurrencyCodeLookup.XBT}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName={'amount'}
                      label={'Amount'}
                      type={'number'}
                      locked={false}
                    />
                  </Grid>
                  <Grid item md={12}>
                    <Button
                      variant={'contained'}
                      type="submit"
                      sx={{ mr: 2 }}
                    >
                      Create Fee
                      {isLoading && <ButtonLoadingComponent/>}
                    </Button>
                    <Button
                      variant={'outlined'}
                      type="button"
                      onClick={() => { toggleDialog(false) }}
                      sx={{ mr: 2 }}
                    >
                      Close
                    </Button>
                  </Grid>
                </Grid>
              </DialogContent>
            </form>
          )}
        </Formik>
      </Dialog>
    </>
  )
}

export default CreateDisbursementFeeComponent
